import { useMemo, useState } from 'react';
import { ACCENT_STYLES } from '../accents';
import type { AccentName } from '../api';

interface Props {
  url: string;
  name: string;
  /** User-provided icon string (e.g. "S", "AB"). Used in fallback only. */
  icon?: string;
  accent: AccentName;
  /** Outer square size in tailwind units (default 14 → 56px) */
  size?: 12 | 14 | 16;
}

/**
 * Tile/row icon block.
 *
 * For internal Surfboard systems the browser sits on the same network as the
 * subdomains, so we fetch favicons directly from each site. External
 * favicon scrapers (Google, DuckDuckGo) are useless here — they either
 * 404 or return a generic globe icon for every domain. We try the common
 * paths in order. If the SPA returns HTML masquerading as a favicon, the
 * <img> decode fails, onError fires, and we fall through.
 */
export function Favicon({ url, name, icon, accent, size = 14 }: Props) {
  const origin = useMemo(() => originOf(url), [url]);
  const accentStyle = ACCENT_STYLES[accent] ?? ACCENT_STYLES.slate;

  const sources = useMemo(() => {
    if (!origin) return [];
    return [
      `${origin}/favicon.svg`,
      `${origin}/favicon.ico`,
      `${origin}/apple-touch-icon.png`,
      `${origin}/apple-touch-icon-precomposed.png`,
      `${origin}/favicon-32x32.png`,
      `${origin}/favicon-192x192.png`,
    ];
  }, [origin]);

  const [srcIdx, setSrcIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const sizeClass = size === 16 ? 'h-16 w-16' : size === 12 ? 'h-12 w-12' : 'h-14 w-14';
  const innerSizeClass = size === 16 ? 'h-9 w-9' : size === 12 ? 'h-7 w-7' : 'h-8 w-8';

  // Exhausted all sources → fallback monogram.
  if (srcIdx >= sources.length) {
    return <Monogram name={name} icon={icon} accent={accent} sizeClass={sizeClass} />;
  }

  return (
    <span
      className={`relative grid ${sizeClass} shrink-0 place-items-center overflow-hidden rounded-sm border hair-strong bg-ink-700`}
    >
      {/* Subtle accent tint behind the icon — fades in once the image lands. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 transition-opacity duration-300"
        style={{
          background: `radial-gradient(circle at 30% 30%, ${accentStyle.hex}1f 0%, transparent 70%)`,
          opacity: loaded ? 1 : 0,
        }}
      />
      {/* Placeholder shimmer while loading — accent block pulsing. */}
      {!loaded && (
        <span className={`${innerSizeClass} animate-pulse-dot rounded-sm ${accentStyle.strip} opacity-40`} />
      )}
      <img
        src={sources[srcIdx]}
        alt=""
        referrerPolicy="no-referrer"
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => {
          setLoaded(false);
          setSrcIdx((i) => i + 1);
        }}
        className={`relative ${innerSizeClass} object-contain transition-opacity duration-300 ${
          loaded ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </span>
  );
}

function Monogram({
  name,
  icon,
  accent,
  sizeClass,
}: {
  name: string;
  icon?: string;
  accent: AccentName;
  sizeClass: string;
}) {
  const accentStyle = ACCENT_STYLES[accent] ?? ACCENT_STYLES.slate;
  const text = (icon?.trim() || initialsOf(name)).slice(0, 2).toUpperCase();

  // Filled accent square with letters in compact Inter Black, ink-coloured for
  // contrast. Reads as an app icon (think Slack, Notion) — not as a
  // typographic flourish floating in the tile.
  return (
    <span
      className={`relative grid ${sizeClass} shrink-0 place-items-center overflow-hidden rounded-sm ${accentStyle.strip} text-ink shadow-[inset_0_-2px_0_rgba(0,0,0,0.22),inset_0_1px_0_rgba(255,255,255,0.18)]`}
      aria-hidden
    >
      {/* Subtle 135° crosshatch so it doesn't read as a flat coloured square. */}
      <span
        className="pointer-events-none absolute inset-0 opacity-25"
        style={{
          background:
            'repeating-linear-gradient(135deg, rgba(0,0,0,0.18) 0 1px, transparent 1px 6px)',
        }}
      />
      {/* Corner notch — small architectural detail */}
      <span
        aria-hidden
        className="pointer-events-none absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-ink/40"
      />
      <span
        className="relative font-sans font-black leading-none"
        style={{
          letterSpacing: '-0.04em',
          fontSize: text.length > 1 ? '14px' : '20px',
        }}
      >
        {text}
      </span>
    </span>
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return parts[0][0] + parts[1][0];
  return name.slice(0, 2);
}

function originOf(url: string): string {
  try {
    return new URL(url).origin;
  } catch {
    return '';
  }
}
